import type { EventListQuery } from "@cdt/shared";
import { CalendarX2, Eye, Flag, Star, X } from "lucide-react";

import { DateRangeInputs } from "@/components/events/DateRangeInputs";
import { SearchInput } from "@/components/events/SearchInput";
import { StatusCheckboxGroup } from "@/components/events/StatusCheckboxGroup";
import { Button } from "@/components/ui/button";
import { isDefaultQuery } from "@/lib/eventsQueryParams";

const PRIORITY_OPTIONS = [
  { value: 0, label: "Non valutati" },
  { value: 1, label: "1 stella" },
  { value: 2, label: "2 stelle" },
  { value: 3, label: "3 stelle" },
];

interface EventsFilterBarProps {
  query: EventListQuery;
  onPatch: (patch: Partial<EventListQuery>) => void;
  onClear: () => void;
}

export function EventsFilterBar({
  query,
  onPatch,
  onClear,
}: EventsFilterBarProps) {
  const hasFilters = !isDefaultQuery(query);

  const activePriorities = query.priority ?? [];

  function togglePriority(v: number) {
    const next = activePriorities.includes(v)
      ? activePriorities.filter((p) => p !== v)
      : [...activePriorities, v];
    onPatch({ priority: next.length > 0 ? next : undefined, page: 1 });
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-end gap-4">
        <StatusCheckboxGroup
          value={query.status}
          onChange={(status) => onPatch({ status })}
        />
        <DateRangeInputs
          from={query.dateFrom}
          to={query.dateTo}
          onChange={onPatch}
        />
        <SearchInput
          value={query.search}
          onChange={(search) => onPatch({ search })}
        />
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <Star className="h-3 w-3" />
            Priorità
          </span>
          <div className="flex items-center gap-2">
            {PRIORITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => togglePriority(opt.value)}
                className={
                  activePriorities.includes(opt.value)
                    ? "rounded border border-amber-400 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                    : "rounded border border-border px-2 py-0.5 text-xs text-muted-foreground hover:border-amber-300 hover:text-foreground"
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <Eye className="h-3 w-3" />
            Visibilità
          </span>
          <label
            className={`inline-flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1 text-sm transition-colors ${
              query.unseen
                ? "border-primary bg-primary/5"
                : "border-input hover:bg-muted"
            }`}
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-primary"
              checked={query.unseen === true}
              onChange={(e) =>
                onPatch({ unseen: e.target.checked ? true : undefined, page: 1 })
              }
            />
            Solo non viste
          </label>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <Flag className="h-3 w-3" />
            Importanza
          </span>
          <label
            className={`inline-flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1 text-sm transition-colors ${
              query.isImportant
                ? "border-rose-400 bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                : "border-input hover:bg-muted"
            }`}
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-rose-500"
              checked={query.isImportant === true}
              onChange={(e) =>
                onPatch({ isImportant: e.target.checked ? true : undefined, page: 1 })
              }
            />
            Solo importanti
          </label>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <CalendarX2 className="h-3 w-3" />
            Durata
          </span>
          <label
            className={`inline-flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1 text-sm transition-colors ${
              query.hideMultiDay
                ? "border-primary bg-primary/5"
                : "border-input hover:bg-muted"
            }`}
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-primary"
              checked={query.hideMultiDay === true}
              onChange={(e) =>
                onPatch({ hideMultiDay: e.target.checked ? true : undefined, page: 1 })
              }
            />
            Nascondi multi-giorno
          </label>
        </div>

        {hasFilters ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="ml-auto"
          >
            <X />
            Pulisci filtri
          </Button>
        ) : null}
      </div>
    </div>
  );
}
